import {Inject, Injectable, InjectionToken, OnDestroy, signal} from '@angular/core';
import {Observable, Subject, Subscription} from 'rxjs';
import * as signalR from '@microsoft/signalr';
import {HubConnectionState} from '@microsoft/signalr';
import {AuthenticationService} from '../../../../_auth/authentication.service';
import {UserJwt} from '../../../../models/admin_owner/user/user-jwt';
import {FiscalJobProgressPush, FiscalJobState} from '../../_models/fiscal-jobs/fiscal-job.models';

/** The server → client method the fiscal job hub pushes on. */
export const FISCAL_JOB_HUB_PUSH_METHOD = 'FiscalJobProgress';

/** The hub's URL, appended to `BASE_URL`. The gateway routes `/hubs/*` (WebSocket) to the monolith. */
export const FISCAL_JOB_HUB_PATH = '/hubs/fiscal-jobs';

/** The connection is up — for the first time, or again after a drop. */
export interface FiscalJobHubConnected {
  kind: 'connected';
}

/**
 * The connection is no longer carrying pushes (Bug #784).
 *
 * Raised when an established connection drops — while SignalR is retrying, and again if it gives up — and
 * when an initial connect fails. **It is not a fault to render**: it says only that the push channel is not
 * delivering, which is the store's cue to arm its own fallback. Every path on the screens works without it.
 */
export interface FiscalJobHubDisconnected {
  kind: 'disconnected';
}

/** Something advanced on a run. */
export interface FiscalJobHubProgress extends FiscalJobProgressPush {
  kind: 'progress';
}

export type FiscalJobHubSignal = FiscalJobHubProgress | FiscalJobHubConnected | FiscalJobHubDisconnected;

/**
 * The slice of `signalR.HubConnection` this client uses.
 *
 * A structural interface rather than the class, for the reason `GateHubConnection` states: a spec must be able
 * to hand in a fake, and a fake of the real class would either open a real socket or be a cast that silently
 * stops matching when a member is added. The real `HubConnection` satisfies this as-is.
 */
export interface FiscalJobHubConnection {
  readonly state: HubConnectionState;

  on(methodName: string, newMethod: (...args: unknown[]) => void): void;

  onreconnected(callback: (connectionId?: string) => void): void;

  /**
   * The connection dropped and automatic reconnection has begun.
   *
   * Wired because `onclose` alone is too late: with `withAutomaticReconnect()` a drop fires this first and
   * only reaches `onclose` after every attempt has failed, roughly forty seconds later on the defaults. The
   * store arms its fallback poll on the first signal, so the screen keeps moving during the retry window
   * instead of after it (Bug #784).
   */
  onreconnecting(callback: (error?: Error) => void): void;

  onclose(callback: (error?: Error) => void): void;

  start(): Promise<void>;

  stop(): Promise<void>;
}

/** Builds the connection. The one seam between this client and `@microsoft/signalr`. */
export type FiscalJobHubConnectionFactory =
  (url: string, accessTokenFactory: () => string) => FiscalJobHubConnection;

/**
 * The production factory: the same `withUrl` shape every other hub in this SPA uses.
 *
 * `accessTokenFactory` is read **per negotiate**, not captured, so a token swapped mid-session is picked up by
 * the next reconnect. The `CM-Secret` cookie rides `withCredentials` — the monolith decrypts the bearer with it
 * on every request, hub negotiate included, and without it the connection is refused before the JWT is read.
 */
export const defaultFiscalJobHubConnectionFactory: FiscalJobHubConnectionFactory =
  (url, accessTokenFactory) => new signalR.HubConnectionBuilder()
    .configureLogging(signalR.LogLevel.Warning)
    .withUrl(url, {withCredentials: true, accessTokenFactory})
    .withAutomaticReconnect()
    .build();

/** Overridable in a spec (and only there) so no test ever opens a socket. */
export const FISCAL_JOB_HUB_CONNECTION_FACTORY = new InjectionToken<FiscalJobHubConnectionFactory>(
  'FISCAL_JOB_HUB_CONNECTION_FACTORY',
  {providedIn: 'root', factory: () => defaultFiscalJobHubConnectionFactory});

/**
 * How long to wait before retrying an INITIAL connect that failed, and how many times.
 *
 * `withAutomaticReconnect()` covers a connection that was established and then dropped; it does **not** retry a
 * `start()` that never succeeded. That case is real: the SPA boots while the API is still coming up, or an
 * older server has no `/hubs/fiscal-jobs` at all. Retrying a bounded number of times covers the first; giving
 * up quietly covers the second, and the store's refetch-on-demand is what keeps the feature correct without a
 * socket.
 */
export const FISCAL_JOB_HUB_CONNECT_RETRY_MS: readonly number[] = [5_000, 15_000, 60_000];

/**
 * Holds the SPA's connection to the **fiscal job progress hub** and turns it into two signals: "a run
 * advanced" and "we are (re)connected" (#768).
 *
 * ### What it never sends
 *
 * Nothing. The client invokes no hub method and passes no ids: the server derives the groups from the
 * connection's own token (company, and shard when one is selected), exactly as `/hubs/gates` does. A client
 * that could name its own group could name somebody else's, and a fiscal run's group would hand it another
 * location's filing activity.
 *
 * ### What a push means
 *
 * "Refetch the run." Nothing more. The durable record on `/fiscal/jobs/get` is the source of truth, which is
 * what makes a reload, a second tab and a visit tomorrow all show the same thing — and what lets a finished
 * export be listed for re-download rather than rebuilt. {@link FiscalJobStore} is the only consumer and does
 * exactly that.
 *
 * ### Lifecycle
 *
 * Driven entirely by `AuthenticationService.user`, on the **principal and its tenant**, not the token: a
 * different account or a different company/shard means the server's groups would be wrong, so the connection
 * is rebuilt; a token swapped by a reissue means nothing, because `accessTokenFactory` reads the live token
 * anyway and reconnecting there would be a reconnect loop.
 */
@Injectable({providedIn: 'root'})
export class FiscalJobHubClient implements OnDestroy {
  /** True while the hub connection is established. Read by the store's degraded-mode decision. */
  readonly connected = signal(false);

  private readonly subject = new Subject<FiscalJobHubSignal>();

  /** Pushes and (re)connections, in arrival order. Hot: a signal raised while nobody listens is lost. */
  readonly signals: Observable<FiscalJobHubSignal> = this.subject.asObservable();

  /** The live connection, or null while there is no session (or between a teardown and the next connect). */
  private connection: FiscalJobHubConnection | null = null;

  /** The `auth.user` subscription — the thing that drives the whole lifecycle. Null while stopped. */
  private sessionSubscription: Subscription | null = null;

  /** The principal + tenant currently connected for, or null. */
  private connectedKey: string | null = null;

  /** Pending initial-connect retry, and how many have been spent. */
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private retryCount = 0;

  constructor(private auth: AuthenticationService, @Inject('BASE_URL') private baseUrl: string,
              @Inject(FISCAL_JOB_HUB_CONNECTION_FACTORY) private factory: FiscalJobHubConnectionFactory) {
  }

  /**
   * Begin following the session. Idempotent — calling it twice does not open a second connection.
   *
   * `auth.user` is a `BehaviorSubject`, so subscribing here immediately delivers whatever session the SPA
   * booted with; a tab reloaded onto a stored session connects without waiting for a login that never comes.
   */
  start(): void {
    if (this.sessionSubscription) return;
    this.sessionSubscription = this.auth.user.subscribe(user => this.onSession(user));
  }

  /** Stop following the session and drop the connection. The client can be {@link start}ed again after. */
  stop(): void {
    this.sessionSubscription?.unsubscribe();
    this.sessionSubscription = null;
    this.connectedKey = null;
    this.teardownConnection();
  }

  ngOnDestroy(): void {
    this.stop();
    this.subject.complete();
  }

  /** A session arrived, went away or changed tenant. Reconnect only when the identity actually moved. */
  private onSession(user: UserJwt | null): void {
    const key = fiscalJobPrincipalKey(user);
    if (key === this.connectedKey) return;

    this.connectedKey = key;
    this.teardownConnection();
    if (key !== null) this.openConnection();
  }

  /** Build, wire and start a connection for the current session. */
  private openConnection(): void {
    const connection = this.factory(this.baseUrl + FISCAL_JOB_HUB_PATH,
      () => this.auth.userValue?.jwt ?? '');
    this.connection = connection;

    connection.on(FISCAL_JOB_HUB_PUSH_METHOD, (...args: unknown[]) => this.onPush(args[0]));
    connection.onreconnected(() => {
      if (this.connection === connection) this.announceConnected();
    });
    // Both halves of a drop are announced, and the guard is what keeps a deliberate teardown silent:
    // `teardownConnection` nulls `this.connection` before stopping it, so a stale connection's callbacks
    // cannot arm a poll for a screen that has just gone away.
    connection.onreconnecting(() => {
      if (this.connection === connection) this.announceDisconnected();
    });
    connection.onclose(() => {
      if (this.connection === connection) this.announceDisconnected();
    });

    connection.start().then(
      () => {
        // A session that changed while `start()` was in flight already tore this one out of `this.connection`.
        // Stopping it here is what stops a stale connection (wrong tenant groups) delivering anything.
        if (this.connection !== connection) {
          connection.stop().catch(() => undefined);
          return;
        }
        this.retryCount = 0;
        this.announceConnected();
      },
      err => {
        if (this.connection !== connection) return;
        this.announceDisconnected();
        this.scheduleConnectRetry(err);
      });
  }

  /**
   * Retry an initial connect that never succeeded, a bounded number of times, then stop trying.
   *
   * Giving up is deliberate and safe: without a socket the store still refetches on demand and on every
   * explicit action, so a run is readable — it just stops updating by itself. An unbounded retry against a
   * server that simply does not have this hub would be a request every minute for as long as the tab is open,
   * for nothing.
   */
  private scheduleConnectRetry(err: unknown): void {
    if (this.retryCount >= FISCAL_JOB_HUB_CONNECT_RETRY_MS.length) {
      console.warn('[fiscal-job-hub] giving up on the progress hub; refetch takes over', err);
      return;
    }

    const delay = FISCAL_JOB_HUB_CONNECT_RETRY_MS[this.retryCount++];
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      if (this.connectedKey !== null && this.connection !== null) this.openConnection();
    }, delay);
  }

  /** Publish "connected", the trigger for a catch-up refetch (a push while down was never delivered). */
  private announceConnected(): void {
    this.connected.set(true);
    this.subject.next({kind: 'connected'});
  }

  /**
   * Publish "disconnected", the trigger for the store's fallback poll (Bug #784).
   *
   * Announced rather than only recorded on {@link connected}: a signal nobody is told about is a signal
   * nobody can react to, and the store used to read `connected` exactly once, when a run was opened. Raised
   * more than once for one outage (reconnecting, then close) on purpose — arming the fallback is idempotent,
   * and a missed drop is the failure that is actually expensive.
   */
  private announceDisconnected(): void {
    this.connected.set(false);
    this.subject.next({kind: 'disconnected'});
  }

  /** Turn whatever the server sent into a {@link FiscalJobHubProgress}, or drop it. */
  private onPush(payload: unknown): void {
    const push = readFiscalJobPush(payload);
    if (push) this.subject.next(push);
  }

  /** Stop and forget the current connection, and cancel a pending retry. Safe when there is none. */
  private teardownConnection(): void {
    if (this.retryTimer !== null) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    this.retryCount = 0;

    const connection = this.connection;
    this.connection = null;
    this.connected.set(false);
    if (connection && connection.state !== HubConnectionState.Disconnected) {
      connection.stop().catch(err => console.warn('[fiscal-job-hub] stop failed', err));
    }
  }
}

/**
 * What identifies the session a connection was opened for — **not** the token.
 *
 * The server derives the hub's groups from the company and shard claims at negotiate time, so the connection
 * has to be rebuilt whenever either moves, and must NOT be rebuilt when only the token string changed.
 *
 * @returns the key, or null when there is no usable session.
 */
export function fiscalJobPrincipalKey(user: UserJwt | null): string | null {
  if (!user?.jwt) return null;
  return [user.id ?? '', user.company?.id ?? '', user.shard?.id ?? ''].join('|');
}

/**
 * Read a push payload defensively.
 *
 * SignalR delivers method arguments positionally, and a payload without a job id is unusable — there is
 * nothing to refetch — so it is dropped rather than turned into a refetch of everything. Anything else is
 * tolerated: an unknown `state` string is kept as-is and never branched on, because the client's reaction to
 * every push is identical.
 *
 * @returns the push, or null when the payload named no run.
 */
export function readFiscalJobPush(payload: unknown): FiscalJobHubProgress | null {
  const record = (payload ?? {}) as Record<string, unknown>;
  const jobId = typeof record['jobId'] === 'string' ? record['jobId'] as string : null;
  if (!jobId) return null;

  const state = typeof record['state'] === 'string' ? record['state'] as FiscalJobState : 'running';
  const occurredUtc = typeof record['occurredUtc'] === 'string' ? record['occurredUtc'] as string : null;
  return {kind: 'progress', jobId, state, occurredUtc};
}
